import { useEffect, useState } from "react";
import { Sparkles, Pause, Play, Pencil, Trash2, Eye, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

const EMPTY = { job_titles: "", skills: "", locations: "", salary_min: "", job_types: [], exclude: "" };

const toFields = (p) => ({
  job_titles: (p?.job_titles || []).join(", "),
  skills: (p?.skills || []).join(", "),
  locations: (p?.locations || []).join(", "),
  salary_min: p?.salary_min || "",
  job_types: p?.job_types || [],
  exclude: (p?.exclude || []).join(", "),
});
const toPrefs = (f) => ({
  job_titles: f.job_titles.split(",").map((s) => s.trim()).filter(Boolean),
  skills: f.skills.split(",").map((s) => s.trim()).filter(Boolean),
  locations: f.locations.split(",").map((s) => s.trim()).filter(Boolean),
  salary_min: f.salary_min ? parseInt(f.salary_min, 10) || null : null,
  job_types: f.job_types,
  exclude: f.exclude.split(",").map((s) => s.trim()).filter(Boolean),
});

const Field = ({ label, hint, ...props }) => (
  <label className="block">
    <span className="text-xs font-semibold text-zinc-700">{label}</span>
    <input {...props}
      className="mt-1 w-full h-10 px-3 border border-zinc-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-600" />
    {hint && <span className="mt-0.5 block text-[11px] text-zinc-400">{hint}</span>}
  </label>
);

export default function CandidateMatching({ user }) {
  const [st, setSt] = useState(null);            // server state
  const [fields, setFields] = useState(EMPTY);   // editable structured prefs
  const [raw, setRaw] = useState("");            // plain-language description
  const [mode, setMode] = useState("view");      // view | edit
  const [busy, setBusy] = useState("");
  const [consent, setConsent] = useState(false);
  const [sample, setSample] = useState(null);    // {subject, html}

  const load = () => api.get("/matching").then((r) => {
    setSt(r.data);
    setFields(toFields(r.data.prefs));
    setRaw(r.data.raw_text || "");
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const act = async (key, fn, okMsg) => {
    setBusy(key);
    try {
      const r = await fn();
      if (r?.data?.prefs !== undefined) { setSt(r.data); setFields(toFields(r.data.prefs)); }
      if (okMsg) toast.success(okMsg);
      return r;
    } catch (e) {
      toast.error(formatApiError(e));
      return null;
    } finally { setBusy(""); }
  };

  const generate = () => act("gen", () => api.post("/matching/parse", { text: raw }).then((r) => {
    setFields(toFields(r.data.prefs));
    toast.success("Preferences built — check every field below and edit anything before saving.");
    return r;
  }));

  const save = (optIn) => act("save", async () => {
    const r = await api.post("/matching/save", { prefs: toPrefs(fields), opt_in: optIn, frequency: st?.frequency || freq, raw_text: raw });
    return r;
  }, optIn ? "AI job matching is on — first email arrives after the next scheduled check." : "Preferences saved.");

  const [freq, setFreq] = useState("daily");
  useEffect(() => { if (st?.frequency) setFreq(st.frequency); }, [st?.frequency]);

  const preview = () => act("prev", async () => {
    await api.post("/matching/save", { prefs: toPrefs(fields), opt_in: !!st?.opt_in, frequency: freq, raw_text: raw });
    const r = await api.get("/matching/sample");
    setSample(r.data);
    return r;
  });

  const stop = () => {
    if (!window.confirm("Stop AI job matching completely? Your preferences and history are deleted — saved job alerts are not affected.")) return;
    act("stop", () => api.delete("/matching").then((r) => {
      setSample(null); setConsent(false); setFields(EMPTY); setRaw(""); setMode("view");
      load();  // refetch server state — the status card clears only from the real response
      return r;
    }), "AI job matching is off and your preferences were deleted.");
  };

  if (!st) return <div className="border border-zinc-200 rounded-md p-8 bg-white text-sm text-zinc-400" data-testid="matching-loading">Loading…</div>;

  const enabled = st.opt_in;
  const editing = mode === "edit" || !enabled;

  return (
    <div className="space-y-5" data-testid="ai-matching">
      {/* status strip when enabled */}
      {enabled && (
        <div className="border border-zinc-200 rounded-md bg-white p-5" data-testid="matching-status">
          <div className="flex flex-wrap items-center gap-3 justify-between">
            <div>
              <p className="text-sm font-semibold text-zinc-900">
                AI job matching is {st.paused ? "paused" : "on"}
                <span className={`ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${st.paused ? "bg-amber-100 text-amber-800" : "bg-green-100 text-green-700"}`} data-testid="matching-status-pill">
                  {st.paused ? "PAUSED" : "ACTIVE"}
                </span>
              </p>
              <p className="mt-1 text-xs text-zinc-500" data-testid="matching-schedule-note">
                {st.frequency === "weekly" ? "Once a week" : "Each morning"}, on a schedule — not continuously — we check newly posted roles
                against your preferences and email only genuine new matches.
                {st.last_sent_at && <> Last email: {new Date(st.last_sent_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}.</>}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setMode("edit")} data-testid="match-edit"
                className="inline-flex items-center gap-1.5 h-9 px-3.5 border border-zinc-300 rounded-md text-xs font-semibold text-zinc-700 bg-white hover:border-zinc-500 transition-colors duration-200">
                <Pencil className="h-3.5 w-3.5" /> Edit
              </button>
              <button onClick={() => act("pause", () => api.post("/matching/pause", { paused: !st.paused }), st.paused ? "Matching resumed." : "Matching paused — no emails until you resume.")}
                disabled={busy === "pause"} data-testid="match-pause"
                className="inline-flex items-center gap-1.5 h-9 px-3.5 border border-zinc-300 rounded-md text-xs font-semibold text-zinc-700 bg-white hover:border-zinc-500 transition-colors duration-200">
                {st.paused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />} {st.paused ? "Resume" : "Pause"}
              </button>
              <button onClick={stop} disabled={busy === "stop"} data-testid="match-stop"
                className="inline-flex items-center gap-1.5 h-9 px-3.5 border border-red-200 rounded-md text-xs font-semibold text-red-700 bg-white hover:bg-red-50 transition-colors duration-200">
                <Trash2 className="h-3.5 w-3.5" /> Stop &amp; delete
              </button>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <>
          {/* plain-language assistant */}
          <div className="border border-zinc-200 rounded-md bg-white p-5" data-testid="match-assistant">
            <h3 className="font-display text-lg font-bold text-zinc-950 flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-blue-700" /> Describe the work you want
            </h3>
            <p className="mt-1.5 text-xs text-zinc-500 leading-relaxed">
              In your own words — job titles, skills, where you can work, the salary you need, full-time or part-time,
              and anything you don't want. The assistant turns it into the editable preferences below.
              You see exactly what will be used before anything is switched on.
            </p>
            <textarea value={raw} onChange={(e) => setRaw(e.target.value)} rows={3} maxLength={1500}
              data-testid="match-description"
              placeholder="e.g. Warehouse operative or picker-packer around Leeds or Bradford, forklift experience, at least £24,000, full-time only, no night-shift agencies"
              className="mt-3 w-full px-3 py-2.5 border border-zinc-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-600" />
            <div className="mt-2.5 flex items-center gap-3">
              <button onClick={generate} disabled={busy === "gen" || raw.trim().length < 10} data-testid="match-generate"
                className="inline-flex items-center gap-2 h-9 px-4 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200 disabled:opacity-50">
                <Sparkles className="h-3.5 w-3.5" /> {busy === "gen" ? "Thinking…" : "Build my preferences"}
              </button>
              <span className="text-[11px] text-zinc-400">Prefer to skip the assistant? Fill the fields below yourself.</span>
            </div>
          </div>

          {/* editable structured preferences — exactly what gets used */}
          <div className="border border-zinc-200 rounded-md bg-white p-5" data-testid="match-form">
            <h3 className="text-sm font-bold text-zinc-950">Your preferences — check and edit freely</h3>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Job titles" hint="Comma-separated, e.g. Warehouse operative, Forklift driver" data-testid="match-titles"
                value={fields.job_titles} onChange={(e) => setFields({ ...fields, job_titles: e.target.value })} />
              <Field label="Skills" hint="Comma-separated, e.g. forklift, picking, CSCS" data-testid="match-skills"
                value={fields.skills} onChange={(e) => setFields({ ...fields, skills: e.target.value })} />
              <Field label="Locations" hint="Towns or regions — remote/nationwide roles still count" data-testid="match-locations"
                value={fields.locations} onChange={(e) => setFields({ ...fields, locations: e.target.value })} />
              <Field label="Minimum salary (£/year)" hint="Roles without a published salary are still included — never guessed" data-testid="match-salary" type="number" min="1000" max="500000"
                value={fields.salary_min} onChange={(e) => setFields({ ...fields, salary_min: e.target.value })} />
              <Field label="Not interested in" hint="Roles, sectors or employers to leave out" data-testid="match-exclude"
                value={fields.exclude} onChange={(e) => setFields({ ...fields, exclude: e.target.value })} />
              <div>
                <span className="text-xs font-semibold text-zinc-700">Work type</span>
                <div className="mt-1.5 flex gap-4">
                  {["Full-time", "Part-time"].map((t) => (
                    <label key={t} className="inline-flex items-center gap-1.5 text-sm text-zinc-700">
                      <input type="checkbox" checked={fields.job_types.includes(t)} data-testid={`match-type-${t === "Full-time" ? "full" : "part"}`}
                        onChange={(e) => setFields({ ...fields, job_types: e.target.checked ? [...fields.job_types, t] : fields.job_types.filter((x) => x !== t) })}
                        className="h-4 w-4 accent-zinc-950" /> {t}
                    </label>
                  ))}
                </div>
                <span className="mt-0.5 block text-[11px] text-zinc-400">Leave both unticked for any type</span>
              </div>
            </div>
            <div className="mt-4">
              <span className="text-xs font-semibold text-zinc-700">How often?</span>
              <div className="mt-1.5 flex flex-col gap-1.5">
                {[["daily", "Each morning — the scheduled check emails genuinely new matches"],
                  ["weekly", "Once a week — a single round-up of the week's new matches"]].map(([v, lbl]) => (
                  <label key={v} className="inline-flex items-center gap-2 text-sm text-zinc-700">
                    <input type="radio" name="match-freq" checked={freq === v} onChange={() => setFreq(v)} data-testid={`match-freq-${v}`}
                      className="h-4 w-4 accent-zinc-950" /> {lbl}
                  </label>
                ))}
              </div>
              <p className="mt-1.5 text-[11px] text-zinc-400" data-testid="match-honesty-note">
                Matching runs on this schedule only — it is not a continuous search, and you'll never get the same role twice.
              </p>
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-3">
              <button onClick={preview} disabled={busy === "prev"} data-testid="match-preview"
                className="inline-flex items-center gap-1.5 h-9 px-4 border border-zinc-300 rounded-md text-xs font-semibold text-zinc-700 bg-white hover:border-zinc-500 transition-colors duration-200">
                <Eye className="h-3.5 w-3.5" /> {busy === "prev" ? "Building…" : "Preview the email"}
              </button>
              {!enabled ? (
                <>
                  <label className="inline-flex items-start gap-2 text-xs text-zinc-600 max-w-md" data-testid="match-consent-wrap">
                    <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="match-consent"
                      className="mt-0.5 h-4 w-4 accent-zinc-950" />
                    <span>I want HiredToday to check newly posted jobs against these preferences on the chosen schedule and email me
                      genuine matches. Optional, no obligations — I can pause or delete it any time. My details are never shared and
                      nothing is ever sent to employers.</span>
                  </label>
                  <button onClick={() => save(true)} disabled={busy === "save" || !consent} data-testid="match-enable"
                    className="inline-flex items-center gap-2 h-9 px-5 bg-blue-700 text-white text-xs font-semibold rounded-md hover:bg-blue-800 transition-colors duration-200 disabled:opacity-50">
                    Start matching
                  </button>
                </>
              ) : (
                <button onClick={() => act("save", () => api.post("/matching/save", { prefs: toPrefs(fields), opt_in: true, frequency: freq, raw_text: raw }).then((r) => { setMode("view"); return r; }), "Preferences updated.")}
                  disabled={busy === "save"} data-testid="match-save"
                  className="inline-flex items-center gap-2 h-9 px-5 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200 disabled:opacity-50">
                  Save changes
                </button>
              )}
            </div>
            {!st.email_verified && (
              <p className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="match-verify-note">
                Matches arrive by email — please verify your email address (banner at the top of your dashboard) before switching this on.
              </p>
            )}
          </div>
        </>
      )}

      {/* sample email preview modal */}
      {sample && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="match-sample-modal">
          <button aria-label="Close preview" onClick={() => setSample(null)} className="absolute inset-0 bg-zinc-950/60 backdrop-blur-sm" data-testid="match-sample-backdrop" />
          <div className="relative w-full max-w-2xl max-h-[85vh] bg-white rounded-xl shadow-2xl border border-zinc-200 flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-zinc-200">
              <div className="min-w-0">
                <p className="text-sm font-bold text-zinc-950 truncate" data-testid="match-sample-subject">Subject: {sample.subject}</p>
                <p className="text-[11px] text-zinc-400">A preview only — nothing has been sent.</p>
              </div>
              <button onClick={() => setSample(null)} data-testid="match-sample-close"
                className="p-1.5 rounded-md text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors duration-200">
                <X className="h-4 w-4" />
              </button>
            </div>
            <iframe title="Sample matching email" sandbox="" srcDoc={sample.html} data-testid="match-sample-frame"
              className="flex-1 w-full bg-zinc-50" style={{ minHeight: "420px" }} />
          </div>
        </div>
      )}
    </div>
  );
}
